import { jStat } from 'jstat';
import { NormSDist, NormSInv } from './lib';
import { getLeastDistance, round } from './number';

const EMPTY_EVALUATION = {};

const WINNER_CALCULATORS: any = {
    HIGHEST_RESULT: (a: any, b: any) => (a.minValue > b.maxValue ? a : b.minValue > a.maxValue ? b : false),
    LOWEST_RESULT: (a: any, b: any) => (a.maxValue < b.minValue ? a : b.maxValue < a.minValue ? b : false),
};

export function calculate(setup: any, variants: Variant[]) {
    return variants.map(variant => {
        const calculator = selectMarginCalculator(setup, variant);
        if (calculator == null) {
            return null;
        }

        return calculateBasic(setup, variant, calculator);
    });
}

export function selectMarginCalculator(setup: any, variant: any) {
    const { distributionFunction } = setup;
    const { sampleSize } = variant;

    switch (distributionFunction) {
        case 'NORMAL':
            return marginNormal;
        case 'T':
            return marginStudent;
        case 'AUTOMATIC':
            return sampleSize <= 30 ? marginStudent : marginNormal;
        default:
            return null;
    }
}

export function marginNormal(confidenceLevel: any, sampleSize: any, result: any, populationSize: any) {
    return marginNormalDetailed(confidenceLevel, sampleSize, result, populationSize).moe;
}

export function marginNormalDetailed(confidenceLevel: any, sampleSize: any, result: any, populationSize: any) {
    const alpha = 1 - confidenceLevel;
    const zValue = Math.abs(NormSInv(alpha / 2));

    const sampleSD = Math.sqrt(result * (1 - result));
    const stdErr = sampleSD / Math.sqrt(sampleSize);
    const finitePopulationCorrection = Math.sqrt((populationSize - sampleSize) / (populationSize - 1));

    const moe = finitePopulationCorrection * zValue * stdErr;

    return {
        sampleSD,
        stdErr,
        zValue,
        moe,
    };
}

export function marginStudent(confidenceLevel: any, sampleSize: any, result: any, populationSize: any) {
    return marginStudentDetailed(confidenceLevel, sampleSize, result, populationSize).moe;
}

export function marginStudentDetailed(confidenceLevel: any, sampleSize: any, result: any, populationSize: any) {
    const alpha = 1 - confidenceLevel;
    const degreesOfFreedom = sampleSize - 1;
    const sampleSD = Math.sqrt(result * (1 - result));
    const error = sampleSD / Math.sqrt(sampleSize);
    const tVal = Math.abs(jStat.studentt.inv(alpha / 2, degreesOfFreedom));
    const finitePopulationCorrection = Math.sqrt((populationSize - sampleSize) / (populationSize - 1));
    const moe = tVal * error * finitePopulationCorrection;

    return {
        alpha,
        degreesOfFreedom,
        sampleSD,
        error,
        tVal,
        result,
        moe,
        finitePopulationCorrection,
    };
}

export function calculateBasic(
    { confidenceLevel, populationSize, threshold }: any,
    { index, sampleSize, result, resultType, label }: any,
    marginCalculator: any
) {
    const variantKey = label.charAt(label.length - 1);

    if (!populationSize || !sampleSize || result === undefined) {
        return null;
    }
    confidenceLevel *= 0.01;
    // let _result = resultType == "percentage" ? Math.ceil((result / 100) * sampleSize) : parseInt(result);
    // Get percentage number for both % and # input types
    if (resultType === 'numeric') {
        result /= sampleSize;
    }
    if (resultType === 'percentage') {
        result *= 0.01;
    }
    // if (!threshold) {
    //     threshold = 0;
    // } else if (threshold) {
    //     threshold *= 0.01;
    // }

    const withPercentage = (value: any) => (value ? `${value}%` : '');
    const resultPercentage = round(result * 100);
    const marginOfError = marginCalculator(confidenceLevel, sampleSize, result, populationSize);
    const marginOfErrorPercentage = round(marginOfError * 100);
    const minValue = result < marginOfError ? 0 : round((result - marginOfError) * 100);
    const maxValue = result + marginOfError < 1.0 ? round((result + marginOfError) * 100) : 100;
    const range = isNaN(minValue) || isNaN(maxValue) ? '' : minValue + '% - ' + maxValue + '%';
    // const likelihoodScore = calcLikelihoodScore(_result, parseInt(sampleSize), parseFloat(threshold));
    const report = `${withPercentage(resultPercentage.toFixed(2))} \u00B1 ${withPercentage(
        marginOfErrorPercentage.toFixed(2)
    )}`;

    return {
        index,
        marginOfErrorPercentage,
        maxValue,
        minValue,
        // likelihoodScore,
        range,
        report,
        result,
        resultPercentage,
        variantKey,
    } as Result;
}

export function getThresholdResult(threshold: any) {
    return {
        minValue: threshold,
        maxValue: threshold,
        resultPercentage: threshold,
        result: threshold / 100.0,
        variantKey: 'THRESHOLD',
    };
}

const evaluateTwoResults = (a: any, b: any, winnerCalculator: any) => {
    if (!a || !b) {
        return EMPTY_EVALUATION;
    }
    const isVariantRun =
        !(isNaN(a.minValue) || isNaN(a.maxValue) || isNaN(b.minValue) || isNaN(b.maxValue)) ||
        !(a.minValue || a.maxValue || b.minValue || b.maxValue);

    if (!isVariantRun) {
        return EMPTY_EVALUATION;
    }

    const aMultiplier = typeof a.multiplier === 'number' && a.multiplier > 0 ? a.multiplier : 1;
    const bMultiplier = typeof b.multiplier === 'number' && b.multiplier > 0 ? b.multiplier : 1;
    const ciThreshold = (2 * NormSDist(Math.abs(a.result - b.result))) / (aMultiplier + bMultiplier);
    const winner = winnerCalculator(a, b);

    return {
        ciThreshold,
        winner,
        isInconclusive: winner === false,
        evaluation: winner === false ? 'INCONCLUSIVE' : winner.variantKey === 'THRESHOLD' ? 'CLEARLY_BELOW' : 'WINNER',
    };
};

export function getWinner(resultsToCompare: any, winnerCalculator: any) {
    if (resultsToCompare.length === 1) {
        return EMPTY_EVALUATION;
    }
    if (resultsToCompare.length === 2) {
        return evaluateTwoResults(resultsToCompare[0], resultsToCompare[1], winnerCalculator);
    }
    // recursive invocation: fist exp vs winner from the remainder of the exps
    const w: any = getWinner(resultsToCompare.slice(1), winnerCalculator);

    if (w.isInconclusive) {
        const potentialWinner: any = evaluateTwoResults(resultsToCompare[0], resultsToCompare[1], winnerCalculator);

        const potentialWinnerVariantKey = potentialWinner?.winner?.variantKey;
        const toCompareVariantKey = resultsToCompare[0]?.variantKey;

        if (potentialWinnerVariantKey && toCompareVariantKey && potentialWinnerVariantKey === toCompareVariantKey) {
            return potentialWinner;
        } else {
            return w;
        }
    }

    return evaluateTwoResults(resultsToCompare[0], w.winner, winnerCalculator);
}

export function getWinningVariant(results: any, goal = 'HIGHEST_RESULT', threshold: any) {
    if (results.length === 0 || results[0] == null) {
        return EMPTY_EVALUATION;
    }

    if (!threshold && results.length === 1) {
        return EMPTY_EVALUATION;
    }

    const winnerCalculator = WINNER_CALCULATORS[goal];
    const winnerEvaluation =
        results.length > 1
            ? getWinner(results, winnerCalculator)
            : { winner: results[0], isInconclusive: false, evaluation: 'WINNER' };

    if (winnerEvaluation.evaluation === 'INCONCLUSIVE') {
        if (!threshold) {
            return winnerEvaluation;
        }
        const thresholdResult = getThresholdResult(threshold);
        const thresholdBeatsAllResults = results.every((result: any) => {
            const winnerAgainstThreshold = getWinner([thresholdResult, result], winnerCalculator);
            return winnerAgainstThreshold.winner && winnerAgainstThreshold.winner.variantKey === 'THRESHOLD';
        });

        if (thresholdBeatsAllResults) {
            return {
                winner: thresholdResult,
                evaluation: 'CLEARLY_BELOW',
                isInconclusive: false,
            };
        }

        return {
            evaluation: 'INCONCLUSIVE',
            isInconclusive: true,
        };
    }

    if (threshold) {
        const winnerAgainstThreshold = getWinner(
            [getThresholdResult(threshold), winnerEvaluation.winner],
            winnerCalculator
        );
        if (!winnerAgainstThreshold.winner) {
            return {
                evaluation: 'INCONCLUSIVE',
                isInconclusive: true,
            };
        }
        if (winnerAgainstThreshold === EMPTY_EVALUATION || winnerAgainstThreshold.winner.variantKey === 'THRESHOLD') {
            return {
                ...winnerAgainstThreshold,
                evaluation: 'CLEARLY_BELOW',
                isInconclusive: false,
            };
        }
        //otherwise, Threshold did not beat this result so winnerEvaluation will get returned.
    }

    return winnerEvaluation;
}

export function calculateSampleSize({
    confidenceLevel,
    populationSize,
    resultMaxValue,
    resultMinValue,
    targetMarginOfError,
}: // threshold,
any) {
    const isRangeDefined = resultMinValue !== undefined && resultMinValue !== null && resultMaxValue !== undefined && resultMaxValue !== null;

    if (!targetMarginOfError || !isRangeDefined) {
        return null;
    }

    if (resultMinValue > resultMaxValue) {
        return null;
    }

    const rangeIncludesMiddle = resultMinValue <= 0.5 && resultMaxValue >= 0.5;

    const probability =
        rangeIncludesMiddle ||
        resultMinValue === undefined ||
        resultMinValue === null ||
        resultMaxValue === undefined ||
        resultMaxValue === null
            ? 0.5
            : getLeastDistance(0.5, resultMinValue, resultMaxValue);

    const zInv = Math.pow(NormSInv(0.5 + confidenceLevel / 2), 2);
    const sampleSizeUnknownPopulation = (zInv * probability * (1 - probability)) / Math.pow(targetMarginOfError, 2);
    const sampleSizeWithPopulationCorrection =
        sampleSizeUnknownPopulation / (sampleSizeUnknownPopulation / populationSize + 1);

    return Math.round(sampleSizeWithPopulationCorrection);
}
